import { useEffect, useMemo, useRef, useState } from "react";
import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { InputNumber } from "primereact/inputnumber";
import { Calendar } from "primereact/calendar";
import { Dropdown } from "primereact/dropdown";
import { Tag } from "primereact/tag";
import { Toast } from "primereact/toast";
import { customerService } from "../../services/CustomerService";
import FinancialAccountService from "../../services/FinancialAccountService";
import SupplierService from "../../services/SupplierService";

const currencyFormatter = new Intl.NumberFormat("tr-TR", {
  style: "currency",
  currency: "TRY",
  maximumFractionDigits: 2,
});
const formatMoney = (val) => currencyFormatter.format(Number(val) || 0);

const isTryFinancialAccount = (account) => {
  const code = String(account?.currencyCode || "TRY").trim().toUpperCase();
  return code === "TRY" || code === "TL";
};

const collectionMethods = [
  { label: "Nakit (Kasa)", value: "Nakit", icon: "pi-money-bill", desc: "Kasa girişi" },
  { label: "Havale / EFT", value: "Havale/EFT", icon: "pi-building", desc: "Banka hesabı" },
  { label: "Kredi Kartı", value: "Kredi Kartı", icon: "pi-credit-card", desc: "POS cihazı" },
  { label: "Sanal POS", value: "Sanal POS", icon: "pi-globe", desc: "Tedarikçi B2B" },
  { label: "Müşteri Çeki", value: "Çek", icon: "pi-id-card", desc: "Portföye çek alımı" },
  { label: "Müşteri Senedi", value: "Senet", icon: "pi-file", desc: "Portföye senet alımı" },
];

export default function QuickCollectionDialog({
  visible,
  onHide,
  customer,
  onSuccess,
}) {
  const [financialAccounts, setFinancialAccounts] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [paymentType, setPaymentType] = useState("Nakit");
  const [selectedAccountId, setSelectedAccountId] = useState(null);
  const [selectedSupplierId, setSelectedSupplierId] = useState(null);
  const [paymentForm, setPaymentForm] = useState({
    amount: 0,
    paymentDate: new Date(),
    description: "",
  });

  // Cheque Details State
  const [chequeDetails, setChequeDetails] = useState({
    bankName: "",
    bankBranch: "",
    checkNumber: "",
    dueDate: null,
    drawer: "",
  });

  // Promissory Note Details State
  const [promissoryDetails, setPromissoryDetails] = useState({
    drawer: "",
    guarantor: "",
    dueDate: null,
  });

  const toast = useRef(null);

  useEffect(() => {
    if (visible && customer) {
      initDialog();
    }
  }, [visible, customer]);

  const loadData = async () => {
    try {
      const [accRes, supRes] = await Promise.all([
        FinancialAccountService.getAll(),
        SupplierService.getAllSuppliers(),
      ]);

      const accounts = Array.isArray(accRes)
        ? accRes
        : Array.isArray(accRes?.data)
        ? accRes.data
        : accRes?.data?.data || [];
      setFinancialAccounts(accounts);

      const supList = Array.isArray(supRes)
        ? supRes
        : Array.isArray(supRes?.data)
        ? supRes.data
        : supRes?.data?.data || [];
      setSuppliers(supList);

      return { accounts, suppliers: supList };
    } catch (err) {
      console.error("Tahsilat verileri yüklenemedi", err);
      return { accounts: [], suppliers: [] };
    }
  };

  const initDialog = async () => {
    setPaymentType("Nakit");
    setSelectedSupplierId(null);

    let accounts = financialAccounts;
    if (!accounts || accounts.length === 0) {
      const data = await loadData();
      accounts = data.accounts;
    }

    const defaultAcc = accounts?.find(
      (a) => isTryFinancialAccount(a) && a.accountType === 1
    );
    setSelectedAccountId(defaultAcc ? defaultAcc.id : null);

    const initialAmount =
      Number(customer?.remainingAmount) > 0
        ? Number(customer?.remainingAmount)
        : Number(customer?.amount) > 0
        ? Number(customer?.amount)
        : Number(customer?.balance) > 0
        ? Number(customer?.balance)
        : 0;

    const custName = customer?.name || customer?.customerName || "";

    setPaymentForm({
      amount: initialAmount,
      paymentDate: new Date(),
      description: customer?.description || (custName ? `${custName} - Tahsilat` : "Müşteri Tahsilatı"),
    });

    setChequeDetails({
      bankName: "",
      bankBranch: "",
      checkNumber: "",
      dueDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000),
      drawer: custName,
    });

    setPromissoryDetails({
      drawer: custName,
      guarantor: "",
      dueDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000),
    });
  };

  // Filter accounts based on selected payment method
  const filteredAccounts = useMemo(() => {
    const tryAccounts = (financialAccounts || []).filter(isTryFinancialAccount);
    if (paymentType === "Nakit") return tryAccounts.filter((a) => a.accountType === 1);
    if (paymentType === "Havale/EFT" || paymentType === "Banka")
      return tryAccounts.filter((a) => a.accountType === 2);
    if (paymentType === "Kredi Kartı")
      return tryAccounts.filter((a) => a.accountType === 4 || a.accountType === 3);
    if (paymentType === "Sanal POS")
      return tryAccounts.filter((a) => a.accountType === 5);
    return [];
  }, [financialAccounts, paymentType]);

  const accountOptions = useMemo(() => {
    return filteredAccounts.map((a) => {
      const typeLabel =
        a.accountType === 1
          ? "Kasa"
          : a.accountType === 2
          ? "Banka"
          : a.accountType === 3
          ? "Firma Kartı"
          : a.accountType === 4
          ? "POS"
          : a.accountType === 5
          ? "Sanal POS"
          : "Hesap";
      return {
        label: `${a.name} (${typeLabel}) - Bakiye: ${formatMoney(a.balance || 0)}`,
        value: a.id,
      };
    });
  }, [filteredAccounts]);

  // Auto-sync account selection when payment method changes
  useEffect(() => {
    if (paymentType === "Sanal POS") return;
    if (filteredAccounts && filteredAccounts.length > 0) {
      const exists = filteredAccounts.some((a) => a.id === selectedAccountId);
      if (!exists) {
        setSelectedAccountId(filteredAccounts[0].id);
      }
    } else {
      setSelectedAccountId(null);
    }
  }, [paymentType, filteredAccounts]);

  const handleSupplierChange = (supplierId) => {
    setSelectedSupplierId(supplierId);
    const matchedAcc = financialAccounts.find(
      (a) => isTryFinancialAccount(a) && a.accountType === 5 && a.supplierId === supplierId
    );
    if (matchedAcc) {
      setSelectedAccountId(matchedAcc.id);
    } else {
      setSelectedAccountId(null);
    }
  };

  const handleAccountChange = (accountId) => {
    setSelectedAccountId(accountId);
    const selectedAcc = financialAccounts.find((a) => a.id === accountId);
    if (selectedAcc && selectedAcc.supplierId) {
      setSelectedSupplierId(selectedAcc.supplierId);
    }
  };

  const handleSaveCollection = async () => {
    if (!paymentForm.amount || paymentForm.amount <= 0) {
      toast.current?.show({
        severity: "warn",
        summary: "Uyarı",
        detail: "Lütfen geçerli bir tahsilat tutarı giriniz.",
      });
      return;
    }

    if (paymentType === "Sanal POS") {
      if (!selectedAccountId && !selectedSupplierId) {
        toast.current?.show({
          severity: "warn",
          summary: "Uyarı",
          detail: "Sanal POS işleminde hesap veya tedarikçi seçilmelidir.",
        });
        return;
      }
    }

    const customerId = customer?.id || customer?.customerId;
    if (!customerId) {
      toast.current?.show({
        severity: "error",
        summary: "Hata",
        detail: "Müşteri bilgisi bulunamadı.",
      });
      return;
    }

    try {
      setSubmitting(true);
      const payload = {
        customerId: parseInt(customerId, 10),
        amount: paymentForm.amount,
        paymentType: paymentType,
        paymentDate: paymentForm.paymentDate,
        description: paymentForm.description,
        financialAccountId: selectedAccountId,
        supplierId: paymentType === "Sanal POS" ? selectedSupplierId : null,
        customerReceivableDueItemId:
          customer?.receivableId || customer?.customerReceivableDueItemId || null,
        ...(paymentType === "Çek" ? chequeDetails : {}),
        ...(paymentType === "Senet" ? promissoryDetails : {}),
      };

      const res = await customerService.receivePayment(payload);
      const paymentResult = res?.data ?? res;
      if (paymentResult?.success === false) {
        throw new Error(paymentResult.message || "Tahsilat kaydedilemedi.");
      }

      const transactionId = paymentResult?.data;

      if (onSuccess) {
        onSuccess({
          transactionId,
          amount: paymentForm.amount,
          paymentType,
          customer,
        });
      }

      onHide();
    } catch (err) {
      toast.current?.show({
        severity: "error",
        summary: "Hata",
        detail: err?.message || "Tahsilat işlemi kaydedilirken bir hata oluştu.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (!customer) return null;

  const customerName = customer?.name || customer?.customerName || "Müşteri";
  const customerBalance = customer?.balance !== undefined ? Number(customer.balance) : null;
  const suggestedAmount =
    Number(customer?.remainingAmount) > 0
      ? Number(customer?.remainingAmount)
      : Number(customer?.amount) > 0
      ? Number(customer?.amount)
      : null;

  return (
    <>
      <Toast ref={toast} appendTo={typeof document !== "undefined" ? document.body : "self"} />
      <Dialog
        visible={visible}
        onHide={() => !submitting && onHide()}
        style={{ width: "95vw", maxWidth: "620px" }}
        modal
        showHeader={false}
        className="p-0 border-none shadow-2xl rounded-3xl overflow-hidden custom-payment-modal"
        content={({ hide }) => (
          <div className="flex flex-col bg-white dark:bg-slate-900 rounded-3xl shadow-2xl overflow-hidden border border-slate-200/80 dark:border-slate-800">
            {/* 1. Header Section - Fintech Dark Gradient with Emerald Glow */}
            <div className="bg-slate-900 text-white p-5 px-6 relative overflow-hidden shrink-0">
              <div className="absolute top-0 right-0 w-64 h-64 bg-emerald-500/10 rounded-full -mr-20 -mt-20 blur-3xl pointer-events-none"></div>

              {/* Fixed Circular Close Button ("X") */}
              <button
                type="button"
                onClick={hide}
                style={{
                  position: "absolute",
                  top: "16px",
                  right: "16px",
                  width: "32px",
                  height: "32px",
                  minWidth: "32px",
                  maxWidth: "32px",
                  minHeight: "32px",
                  maxHeight: "32px",
                  borderRadius: "9999px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  padding: 0,
                  border: "1px solid rgba(255, 255, 255, 0.15)",
                  backgroundColor: "rgba(255, 255, 255, 0.1)",
                  color: "rgba(255, 255, 255, 0.8)",
                  cursor: "pointer",
                  zIndex: 50,
                  flexShrink: 0,
                }}
                className="hover:!bg-white/25 hover:!text-white transition-all"
                disabled={submitting}
                title="Kapat"
              >
                <i className="pi pi-times text-xs" />
              </button>

              <div className="relative z-10 space-y-3">
                <div className="flex items-center gap-3 pr-10">
                  {/* Fixed Size Inbound Icon Badge */}
                  <div
                    style={{
                      width: "42px",
                      height: "42px",
                      minWidth: "42px",
                      maxWidth: "42px",
                      minHeight: "42px",
                      maxHeight: "42px",
                      flexShrink: 0,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      borderRadius: "14px",
                      backgroundColor: "#10b981",
                      boxShadow: "0 10px 15px -3px rgba(16, 185, 129, 0.3)",
                    }}
                  >
                    <i className="pi pi-arrow-down-left text-white text-lg" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <span className="text-[10px] font-black text-emerald-400 uppercase tracking-widest block leading-tight">
                      HIZLI TAHSİLAT PLANI
                    </span>
                    <h2 className="text-base font-black text-white leading-tight truncate m-0 mt-0.5">
                      {customerName}
                    </h2>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3 pt-1">
                  <div className="bg-white/5 backdrop-blur-md border border-white/10 rounded-xl p-2.5 px-3">
                    <span className="text-[9px] font-bold text-slate-400 uppercase tracking-wider block mb-0.5">
                      İŞLEM TÜRÜ
                    </span>
                    <div className="text-xs font-bold text-slate-200 truncate flex items-center gap-1.5">
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-black bg-emerald-500/20 text-emerald-300 border border-emerald-500/30">
                        <i className="pi pi-user text-[9px]" /> Müşteri Tahsilatı
                      </span>
                    </div>
                  </div>
                  <div className="bg-white/5 backdrop-blur-md border border-white/10 rounded-xl p-2.5 px-3">
                    <span className="text-[9px] font-bold text-slate-400 uppercase tracking-wider block mb-0.5">
                      {suggestedAmount !== null ? "BEKLEYEN ALACAK" : "GÜNCEL BAKİYE"}
                    </span>
                    <div className="text-sm font-black text-emerald-400">
                      {formatMoney(suggestedAmount !== null ? suggestedAmount : customerBalance || 0)}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* 2. Scrollable Body Content */}
            <div className="p-5 px-6 space-y-4 overflow-y-auto max-h-[calc(85vh-200px)] custom-scrollbar bg-slate-50/40 dark:bg-slate-900">
              {/* Payment Method Selector Grid */}
              <div className="space-y-1.5">
                <label className="text-[11px] font-black text-slate-500 dark:text-slate-400 uppercase tracking-wider flex items-center gap-1.5">
                  <i className="pi pi-th-large text-emerald-500 text-xs" /> Tahsilat Yöntemi Seçin{" "}
                  <span className="text-red-500">*</span>
                </label>

                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5">
                  {collectionMethods.map((m) => {
                    const isSelected = paymentType === m.value;
                    return (
                      <button
                        key={m.value}
                        type="button"
                        onClick={() => setPaymentType(m.value)}
                        className={`group relative flex items-center gap-2.5 p-2.5 rounded-2xl border-2 transition-all duration-200 text-left cursor-pointer ${
                          isSelected
                            ? "border-emerald-500 bg-white dark:bg-slate-800 shadow-md shadow-emerald-500/10 ring-1 ring-emerald-500/30"
                            : "border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800/80 hover:border-slate-300 dark:hover:border-slate-600 hover:bg-slate-50/80"
                        }`}
                        style={{ minHeight: "52px" }}
                      >
                        <div
                          className={`rounded-xl flex items-center justify-center transition-all ${
                            isSelected
                              ? "bg-emerald-500 text-white shadow-sm shadow-emerald-500/30"
                              : "bg-slate-100 dark:bg-slate-700 text-slate-500 dark:text-slate-400 group-hover:bg-slate-200"
                          }`}
                          style={{
                            width: "32px",
                            height: "32px",
                            minWidth: "32px",
                            maxWidth: "32px",
                            minHeight: "32px",
                            maxHeight: "32px",
                            flexShrink: 0,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <i className={`pi ${m.icon} text-xs`} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between">
                            <span
                              className={`text-xs font-black truncate leading-tight ${
                                isSelected
                                  ? "text-emerald-900 dark:text-emerald-200"
                                  : "text-slate-700 dark:text-slate-200"
                              }`}
                            >
                              {m.label}
                            </span>
                            {isSelected && (
                              <i className="pi pi-check text-emerald-500 text-[10px] font-bold shrink-0 ml-1" />
                            )}
                          </div>
                          <p className="text-[10px] text-slate-400 font-medium m-0 truncate mt-0.5 leading-tight">
                            {m.desc}
                          </p>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Dynamic Sanal POS Supplier & Account Link Section */}
              {paymentType === "Sanal POS" && (
                <div className="bg-indigo-50/60 dark:bg-indigo-950/20 p-4 rounded-2xl border border-indigo-200/80 dark:border-indigo-900/40 space-y-3 animate-fade-in">
                  <div className="text-[11px] font-black uppercase text-indigo-800 dark:text-indigo-300 flex items-center gap-1.5">
                    <i className="pi pi-globe text-xs" /> TEDARİKÇİ B2B SANAL POS ENTEGRASYONU
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                    <div className="flex flex-col gap-1">
                      <label className="text-[10px] font-bold text-slate-500">
                        Çekim Yapılan Tedarikçi <span className="text-red-500">*</span>
                      </label>
                      <Dropdown
                        value={selectedSupplierId}
                        options={suppliers}
                        optionLabel="name"
                        optionValue="id"
                        filter
                        onChange={(e) => handleSupplierChange(e.value)}
                        placeholder="Tedarikçi Seçiniz..."
                        appendTo={typeof document !== "undefined" ? document.body : "self"}
                        className="w-full rounded-xl border-slate-200 dark:border-slate-700 text-xs font-bold bg-white dark:bg-slate-800"
                        panelClassName="custom-dropdown-panel z-[50000] shadow-2xl rounded-xl"
                      />
                    </div>

                    <div className="flex flex-col gap-1">
                      <label className="text-[10px] font-bold text-slate-500">
                        İlişkili Sanal POS Hesabı
                      </label>
                      <Dropdown
                        value={selectedAccountId}
                        options={filteredAccounts}
                        optionLabel="name"
                        optionValue="id"
                        onChange={(e) => handleAccountChange(e.value)}
                        placeholder="Sanal POS Hesabı..."
                        appendTo={typeof document !== "undefined" ? document.body : "self"}
                        className="w-full rounded-xl border-slate-200 dark:border-slate-700 text-xs font-bold bg-white dark:bg-slate-800"
                        panelClassName="custom-dropdown-panel z-[50000] shadow-2xl rounded-xl"
                        emptyMessage="Bu tedarikçiye ait Sanal POS hesabı yoksa sistem otomatik oluşturacaktır."
                      />
                    </div>
                  </div>
                  <p className="text-[10px] text-indigo-600 dark:text-indigo-400 font-semibold m-0">
                    💡 Müşteri seçilen tedarikçinin sanal posuna ödeme yaptığında, hem müşterinin borcu hem de tedarikçiye olan borcunuz otomatik mahsup edilir.
                  </p>
                </div>
              )}

              {/* Dynamic Form Subsections for Çek */}
              {paymentType === "Çek" && (
                <div className="bg-teal-50/60 dark:bg-teal-950/20 p-4 rounded-2xl border border-teal-200/80 dark:border-teal-900/40 space-y-3 animate-fade-in">
                  <div className="text-[11px] font-black uppercase text-teal-800 dark:text-teal-300 flex items-center gap-1.5">
                    <i className="pi pi-id-card text-xs" /> MÜŞTERİ ÇEK BİLGİLERİ
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                    <div className="flex flex-col gap-1">
                      <label className="text-[10px] font-bold text-slate-500">Banka Adı</label>
                      <InputText
                        value={chequeDetails.bankName}
                        onChange={(e) =>
                          setChequeDetails({ ...chequeDetails, bankName: e.target.value })
                        }
                        placeholder="Örn: Garanti BBVA"
                        className="w-full py-2 rounded-xl text-xs font-bold border-slate-200 dark:border-slate-700"
                      />
                    </div>
                    <div className="flex flex-col gap-1">
                      <label className="text-[10px] font-bold text-slate-500">Şube</label>
                      <InputText
                        value={chequeDetails.bankBranch}
                        onChange={(e) =>
                          setChequeDetails({ ...chequeDetails, bankBranch: e.target.value })
                        }
                        placeholder="Örn: Kadıköy Şb."
                        className="w-full py-2 rounded-xl text-xs font-bold border-slate-200 dark:border-slate-700"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5">
                    <div className="flex flex-col gap-1">
                      <label className="text-[10px] font-bold text-slate-500">Çek No</label>
                      <InputText
                        value={chequeDetails.checkNumber}
                        onChange={(e) =>
                          setChequeDetails({ ...chequeDetails, checkNumber: e.target.value })
                        }
                        placeholder="Örn: 004821"
                        className="w-full py-2 rounded-xl text-xs font-bold border-slate-200 dark:border-slate-700"
                      />
                    </div>
                    <div className="flex flex-col gap-1">
                      <label className="text-[10px] font-bold text-slate-500">Vade Tarihi</label>
                      <Calendar
                        value={chequeDetails.dueDate}
                        onChange={(e) =>
                          setChequeDetails({ ...chequeDetails, dueDate: e.value })
                        }
                        showIcon
                        dateFormat="dd.mm.yy"
                        appendTo={typeof document !== "undefined" ? document.body : "self"}
                        className="w-full custom-emerald-calendar"
                        buttonClassName="!bg-emerald-600 hover:!bg-emerald-700 !border-emerald-600 !text-white !rounded-r-xl !px-3"
                        inputClassName="py-2 rounded-l-xl text-xs font-bold border-slate-200 dark:border-slate-700"
                        panelClassName="z-[50000] bg-white dark:bg-slate-900 shadow-2xl rounded-xl"
                      />
                    </div>
                    <div className="flex flex-col gap-1">
                      <label className="text-[10px] font-bold text-slate-500">Keşideci / Borçlu</label>
                      <InputText
                        value={chequeDetails.drawer}
                        onChange={(e) =>
                          setChequeDetails({ ...chequeDetails, drawer: e.target.value })
                        }
                        placeholder="Müşteri / Şirket Ünvanı"
                        className="w-full py-2 rounded-xl text-xs font-bold border-slate-200 dark:border-slate-700"
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Dynamic Form Subsections for Senet */}
              {paymentType === "Senet" && (
                <div className="bg-cyan-50/60 dark:bg-cyan-950/20 p-4 rounded-2xl border border-cyan-200/80 dark:border-cyan-900/40 space-y-3 animate-fade-in">
                  <div className="text-[11px] font-black uppercase text-cyan-800 dark:text-cyan-300 flex items-center gap-1.5">
                    <i className="pi pi-file text-xs" /> MÜŞTERİ SENET BİLGİLERİ
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5">
                    <div className="flex flex-col gap-1">
                      <label className="text-[10px] font-bold text-slate-500">
                        Borçlu (Düzenleyen)
                      </label>
                      <InputText
                        value={promissoryDetails.drawer}
                        onChange={(e) =>
                          setPromissoryDetails({ ...promissoryDetails, drawer: e.target.value })
                        }
                        placeholder="Müşteri / Şirket Adı"
                        className="w-full py-2 rounded-xl text-xs font-bold border-slate-200 dark:border-slate-700"
                      />
                    </div>
                    <div className="flex flex-col gap-1">
                      <label className="text-[10px] font-bold text-slate-500">Kefil</label>
                      <InputText
                        value={promissoryDetails.guarantor}
                        onChange={(e) =>
                          setPromissoryDetails({
                            ...promissoryDetails,
                            guarantor: e.target.value,
                          })
                        }
                        placeholder="Varsa kefil adı"
                        className="w-full py-2 rounded-xl text-xs font-bold border-slate-200 dark:border-slate-700"
                      />
                    </div>
                    <div className="flex flex-col gap-1">
                      <label className="text-[10px] font-bold text-slate-500">
                        Senet Vade Tarihi
                      </label>
                      <Calendar
                        value={promissoryDetails.dueDate}
                        onChange={(e) =>
                          setPromissoryDetails({ ...promissoryDetails, dueDate: e.value })
                        }
                        showIcon
                        dateFormat="dd.mm.yy"
                        appendTo={typeof document !== "undefined" ? document.body : "self"}
                        className="w-full custom-emerald-calendar"
                        buttonClassName="!bg-emerald-600 hover:!bg-emerald-700 !border-emerald-600 !text-white !rounded-r-xl !px-3"
                        inputClassName="py-2 rounded-l-xl text-xs font-bold border-slate-200 dark:border-slate-700"
                        panelClassName="z-[50000] bg-white dark:bg-slate-900 shadow-2xl rounded-xl"
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Core Fields */}
              <div className="space-y-3 pt-1">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {/* Collection Amount */}
                  <div className="flex flex-col gap-1">
                    <label className="text-xs font-bold text-slate-700 dark:text-slate-300 flex items-center justify-between">
                      <span>Tahsil Edilecek Tutar (TL)</span>
                      <span className="text-red-500">*</span>
                    </label>
                    <InputNumber
                      value={paymentForm.amount}
                      onValueChange={(e) =>
                        setPaymentForm({ ...paymentForm, amount: e.value })
                      }
                      mode="currency"
                      currency="TRY"
                      locale="tr-TR"
                      min={0.01}
                      className="w-full"
                      inputClassName="rounded-xl border-slate-200 dark:border-slate-700 py-2.5 font-black text-sm text-slate-800 dark:text-white bg-white dark:bg-slate-800"
                    />
                  </div>

                  {/* Payment Date */}
                  <div className="flex flex-col gap-1">
                    <label className="text-xs font-bold text-slate-700 dark:text-slate-300 flex items-center justify-between">
                      <span>İşlem Tarihi</span>
                      <span className="text-red-500">*</span>
                    </label>
                    <Calendar
                      value={paymentForm.paymentDate}
                      onChange={(e) =>
                        setPaymentForm({ ...paymentForm, paymentDate: e.value })
                      }
                      showIcon
                      dateFormat="dd.mm.yy"
                      appendTo={typeof document !== "undefined" ? document.body : "self"}
                      className="w-full custom-emerald-calendar"
                      buttonClassName="!bg-emerald-600 hover:!bg-emerald-700 !border-emerald-600 !text-white !rounded-r-xl !px-3"
                      inputClassName="rounded-l-xl border-slate-200 dark:border-slate-700 py-2.5 font-bold text-xs bg-white dark:bg-slate-800"
                      panelClassName="z-[50000] bg-white dark:bg-slate-900 shadow-2xl border border-slate-200 dark:border-slate-700 rounded-xl"
                    />
                  </div>
                </div>

                {/* Financial Account (Kasa / Banka / POS) */}
                {paymentType !== "Sanal POS" && (
                  <div className="flex flex-col gap-1">
                    <label className="text-xs font-bold text-slate-700 dark:text-slate-300">
                      {paymentType === "Nakit"
                        ? "Paranın Giriş Yapacağı Kasa Hesabı"
                        : paymentType === "Havale/EFT" || paymentType === "Banka"
                        ? "Paranın Giriş Yapacağı Banka Hesabı"
                        : paymentType === "Kredi Kartı"
                        ? "Tahsilatın Yapıldığı Firma POS Cihazı / Hesabı"
                        : "İlişkili Hesap (Opsiyonel)"}
                    </label>
                    <Dropdown
                      value={selectedAccountId}
                      options={accountOptions}
                      onChange={(e) => setSelectedAccountId(e.value)}
                      placeholder="Hesap Seçiniz..."
                      appendTo={typeof document !== "undefined" ? document.body : "self"}
                      className="w-full rounded-xl border-slate-200 dark:border-slate-700 text-xs font-bold bg-white dark:bg-slate-800"
                      panelClassName="custom-dropdown-panel shadow-2xl border border-slate-200 dark:border-slate-700 rounded-xl bg-white dark:bg-slate-900"
                      emptyMessage="Bu tahsilat yöntemine uygun aktif hesap bulunamadı."
                    />
                  </div>
                )}

                {/* Description */}
                <div className="flex flex-col gap-1">
                  <label className="text-xs font-bold text-slate-700 dark:text-slate-300">
                    Açıklama / Not
                  </label>
                  <InputText
                    value={paymentForm.description}
                    onChange={(e) =>
                      setPaymentForm({ ...paymentForm, description: e.target.value })
                    }
                    placeholder="Tahsilat açıklaması giriniz..."
                    className="w-full rounded-xl border-slate-200 dark:border-slate-700 py-2.5 text-xs font-bold bg-white dark:bg-slate-800"
                  />
                </div>
              </div>
            </div>

            {/* 3. Solid Fixed Footer */}
            <div className="p-4 px-6 bg-white dark:bg-slate-900 border-t border-slate-200/80 dark:border-slate-800 flex items-center justify-between shrink-0">
              <div className="text-xs text-slate-400 font-medium">
                Alacak / Bakiye:{" "}
                <span className="font-bold text-slate-700 dark:text-slate-200">
                  {formatMoney(suggestedAmount !== null ? suggestedAmount : customerBalance || 0)}
                </span>
              </div>
              <div className="flex items-center gap-2.5">
                <Button
                  label="Vazgeç"
                  icon="pi pi-times"
                  className="p-button-text p-button-secondary !rounded-xl !text-xs !font-bold !py-2.5 !px-4 hover:!bg-slate-100 dark:hover:!bg-slate-800"
                  onClick={hide}
                  disabled={submitting}
                />
                <Button
                  label="Tahsilatı Tamamla"
                  icon="pi pi-check"
                  className="!bg-gradient-to-r !from-emerald-600 !to-teal-600 hover:!from-emerald-700 hover:!to-teal-700 !border-none !rounded-xl !text-xs !font-black !px-6 !py-2.5 text-white shadow-lg shadow-emerald-600/25 transition-all"
                  onClick={handleSaveCollection}
                  loading={submitting}
                />
              </div>
            </div>
          </div>
        )}
      />
    </>
  );
}
